import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

function release(state) {
  cancelAnimationFrame(state.raf);
  state.stream?.getTracks().forEach((track) => track.stop());
  if (state.ctx && state.ctx.state !== "closed") state.ctx.close().catch(() => console.warn("Microphone audio context could not close."));
}

function listen(state, onClap) {
  const analyser = state.ctx.createAnalyser(); analyser.fftSize = 512;
  state.ctx.createMediaStreamSource(state.stream).connect(analyser);
  const data = new Uint8Array(analyser.fftSize);
  const loop = () => {
    analyser.getByteTimeDomainData(data);
    const peak = data.reduce((max, value) => Math.max(max, Math.abs(value - 128) / 128), 0);
    const now = performance.now();
    if (peak > .45 && state.armed && now - state.last > 120) {
      state.last = now; state.armed = false; state.times = state.times.filter((time) => now - time < 900);
      state.times.push(now);
      if (state.times.length >= 2) { state.times = []; onClap(); }
    }
    if (peak < .18) state.armed = true;
    state.raf = requestAnimationFrame(loop);
  };
  loop();
}

export function useClapWake(onClap) {
  const [clapOn, setClapOn] = useState(false);
  const audio = useRef({}), active = useRef(true), pending = useRef(false);
  const stop = useCallback(() => { release(audio.current); audio.current = {}; }, []);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; stop(); };
  }, [stop]);
  const toggleClap = useCallback(async () => {
    if (pending.current) return;
    if (clapOn) { stop(); setClapOn(false); return; }
    pending.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!active.current) { stream.getTracks().forEach((track) => track.stop()); return; }
      audio.current = { stream, ctx: null };
      audio.current.ctx = new (window.AudioContext || window.webkitAudioContext)();
      Object.assign(audio.current, { armed: true, last: 0, times: [] });
      listen(audio.current, onClap); setClapOn(true);
      toast.message("Clap-wake armed — clap twice to summon me, Sir.");
    } catch { stop(); if (active.current) toast.error("Microphone could not be started. Check browser permissions."); }
    finally { pending.current = false; }
  }, [clapOn, onClap, stop]);
  return { clapOn, toggleClap };
}
